import React, { useState, useEffect } from 'react';
import { Typography, Button, Box, useTheme, useMediaQuery } from '@mui/material';

/**
 * OTP Timer component that shows a countdown and enables resend after timeout
 * @param {Object} props
 * @param {number} props.initialTime - Initial time in seconds (default: 30)
 * @param {Function} props.onResend - Function to call when resend is clicked
 * @param {boolean} props.isResending - Flag to indicate if resend is in progress
 */
const OtpTimer = ({ initialTime = 30, onResend, isResending = false }) => {
  const [timeLeft, setTimeLeft] = useState(initialTime);
  const [canResend, setCanResend] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    if (timeLeft > 0) {
      const timerId = setTimeout(() => {
        setTimeLeft(timeLeft - 1);
      }, 1000);
      return () => clearTimeout(timerId);
    } else {
      setCanResend(true);
    }
  }, [timeLeft]);

  // Reset timer when resend is clicked
  const handleResend = () => {
    if (canResend && !isResending) {
      setTimeLeft(initialTime);
      setCanResend(false);
      onResend();
    }
  };

  // Format time as MM:SS
  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds < 10 ? '0' : ''}${remainingSeconds}`;
  };

  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column',
      alignItems: 'center', 
      justifyContent: 'center', 
      mt: 1,
      mb: 1,
      width: '100%',
      maxWidth: '280px'
    }}>
      {!canResend ? (
        <Typography 
          variant="body2" 
          color="text.secondary" 
          sx={{ 
            textAlign: 'center',
            fontSize: isMobile ? '0.75rem' : '0.875rem',
            px: 1
          }}
        >
          Didn't receive OTP? Resend in {formatTime(timeLeft)}
        </Typography>
      ) : (
        <Button 
          onClick={handleResend} 
          disabled={isResending}
          variant="outlined" 
          size={isMobile ? "small" : "medium"}
          color="primary"
          sx={{ 
            borderRadius: 2,
            px: 2,
            py: 0.5,
            fontWeight: 'medium',
            fontSize: isMobile ? '0.75rem' : '0.875rem'
          }}
        >
          {isResending ? 'Sending...' : 'Resend OTP'}
        </Button>
      )}
    </Box>
  );
};

export default OtpTimer; 